import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BalanceRing from "@/components/BalanceRing";
import { STATUS_STYLES, kg, money } from "@/lib/calc";
import { lineColor, lineEmoji, productEmoji, shiftLabel } from "@/lib/ui";
import { exportWeekly, exportShift, exportDaily } from "@/lib/excel";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Download, Camera, PlusCircle, AlertTriangle, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DashboardTour, tourSeen } from "@/components/CoachTip";
import AccuracyCard from "@/components/AccuracyCard";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function Dashboard() {
  const { canSeeMoney } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [week, setWeek] = useState(null);
  const [drill, setDrill] = useState(null);
  const [tour, setTour] = useState(false);

  const load = (wk) => api.get("/dashboard", { params: wk != null ? { week: wk } : {} }).then((r) => { setData(r.data); setWeek(r.data.week); });
  useEffect(() => { const w = searchParams.get("week"); load(w != null && w !== "" ? Number(w) : undefined); if (!tourSeen("dashboard")) setTimeout(() => setTour(true), 800); }, []);

  if (!data) return <div className="p-6 text-teal-700">Loading dashboard…</div>;

  return (
    <div className="p-4 md:p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight text-slate-800">Dashboard</h1>
          <p className="text-slate-400 text-sm">Production balance overview</p>
        </div>
        <div className="flex items-center gap-1 bg-white rounded-full border border-slate-200 shadow-sm">
          <button data-testid="week-prev" onClick={() => load(week - 1)} className="p-2.5 active:scale-90 transition-transform text-teal-600"><ChevronLeft size={18} strokeWidth={2.5} /></button>
          <span className="font-num font-semibold text-slate-700 px-1 text-sm">Week {week}</span>
          <button data-testid="week-next" onClick={() => load(week + 1)} className="p-2.5 active:scale-90 transition-transform text-teal-600"><ChevronRight size={18} strokeWidth={2.5} /></button>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-white rounded-3xl border border-slate-100 p-6 flex flex-col sm:flex-row items-center gap-6 shadow-sm">
        <BalanceRing pct={data.balanced_pct} label={`${data.balanced_pct}%`} sub="allocated" />
        <div className="flex-1 w-full grid grid-cols-2 gap-3">
          <Stat label="Weekly kg" value={kg(data.total_kg)} testid="stat-weekly-kg" />
          <Stat label="Allocated kg" value={kg(data.allocated_kg)} testid="stat-allocated-kg" />
          <Stat label="Balanced shifts" value={`${data.balanced_shifts}/${data.total_shifts}`} testid="stat-balanced" />
          {canSeeMoney ? <Stat label="Production value" value={money(data.production_value)} accent testid="stat-value" />
            : <Stat label="Shifts filed" value={data.total_shifts} testid="stat-shifts" />}
        </div>
      </div>

      <AccuracyCard week={week} />

      {/* Week strip */}
      <div className="bg-white rounded-3xl border border-slate-100 p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-bold text-slate-800">Week {week} · Sun → Sat</h3>
          <Button size="sm" variant="outline" data-testid="export-weekly" onClick={() => exportWeekly(data.shift_cards, week, canSeeMoney)} className="gap-1.5 rounded-full border-teal-200 text-teal-700">
            <Download size={15} strokeWidth={2.5} /> Weekly xlsx
          </Button>
        </div>
        <p className="text-[11px] text-slate-400 mb-2">Crew earned = kilos the crew earned · Given a home = kilos allocated to products · tap the ⬇ on a day for its sheet</p>
        <div className="grid grid-cols-7 gap-2">
          {data.week_strip.map((d, i) => {
            const cls = d.status === "filed" ? "bg-leaf border-emerald-300" : d.status === "attention" ? "bg-amber border-yellow-300" : "bg-slate-50 border-slate-200";
            return (
              <div key={i} data-testid={`daystrip-${i}`} className={`rounded-2xl border ${cls} p-2 text-center relative`}>
                <div className="text-[11px] font-semibold text-slate-600">{DAYS[i]}</div>
                <div className="font-num text-slate-800 font-semibold text-sm mt-1">{d.count || "–"}</div>
                <div className="text-[10px] text-slate-500 font-num">{d.kg ? kg(d.kg) : ""}</div>
                {d.count > 0 && (
                  <button data-testid={`daystrip-export-${i}`} onClick={() => exportDaily(data.shift_cards, d.date, canSeeMoney)}
                    className="absolute -top-1.5 -right-1.5 bg-white border border-teal-200 text-teal-600 rounded-full w-6 h-6 grid place-items-center shadow-sm active:scale-90 transition-transform" title="Daily xlsx">
                    <Download size={12} strokeWidth={2.5} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Shift cards */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-slate-800">Shifts</h3>
          <Button size="sm" data-testid="new-entry-cta" onClick={() => navigate("/new")} className="gap-1.5 btn-grad text-white rounded-full"><PlusCircle size={16} strokeWidth={2.5} /> New Entry</Button>
        </div>
        {data.shift_cards.length === 0 && <div className="text-slate-400 text-sm bg-white border border-slate-100 rounded-2xl p-8 text-center">No shifts filed for this week yet.</div>}
        {data.shift_cards.map((e) => {
          const st = STATUS_STYLES[e.status] || STATUS_STYLES.BALANCED;
          const col = lineColor(e.line);
          return (
            <button key={e.id} data-testid={`shift-card-${e.id}`} onClick={() => setDrill(e)}
              className="w-full text-left bg-white rounded-2xl border border-slate-100 shadow-sm p-4 active:scale-[0.99] transition-transform" style={{ borderLeft: `5px solid ${col}` }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <span className="text-2xl">{lineEmoji(e.line)}</span>
                  <div>
                    <div className="font-heading font-bold text-slate-800">{e.line} · <span className="font-medium text-slate-500">{shiftLabel(e.shift)}</span></div>
                    <div className="text-xs text-slate-400 font-num">{e.date} · {e.workers.length} workers · {(e.allocations || []).length} products</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">Logged by <span className="font-medium text-slate-500">{e.saved_by_name || "—"}</span>{e.filed_for_name ? <> for <span className="font-medium text-indigo-600" data-testid={`filed-for-${e.id}`}>{e.filed_for_name}</span></> : null}</div>
                  </div>
                </div>
                <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${st.bg} ${st.text}`}>{st.label}</span>
              </div>
              <div className="flex gap-4 mt-3 text-sm">
                <span className="text-slate-400">Crew earned <b className="font-num text-slate-700">{kg(e.total_worked)}</b></span>
                <span className="text-slate-400">Given a home <b className="font-num text-slate-700">{kg(e.total_allocated)}</b></span>
                {canSeeMoney && (e.unpriced_kg > 0
                  ? <span className="text-amber-600 inline-flex items-center gap-1 font-medium"><AlertTriangle size={13} /> no price set</span>
                  : e.production_value != null && <span className="text-slate-400">Value <b className="font-num text-teal-700">{money(e.production_value)}</b></span>)}
              </div>
              {e.status !== "BALANCED" && e.status !== "NO WORKERS" && e.status !== "NO PRODUCTS" && Math.abs((e.total_worked || 0) - (e.total_allocated || 0)) > 0.01 && (
                <div data-testid={`hint-${e.id}`} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 rounded-full px-3 py-1">
                  {kg(Math.abs((e.total_worked || 0) - (e.total_allocated || 0)))} kg still need a home <ArrowRight size={12} strokeWidth={3} /> tap to finish
                </div>
              )}
              {(e.status === "NO PRODUCTS") && (
                <div data-testid={`hint-${e.id}`} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 rounded-full px-3 py-1">
                  No products yet <ArrowRight size={12} strokeWidth={3} /> tap to give the kilos a home
                </div>
              )}
              {e.later_reason && e.status !== "BALANCED" && (
                <div data-testid={`later-reason-${e.id}`} className="mt-1.5 text-xs text-amber-800">Finishing later: {e.later_reason}</div>
              )}
            </button>
          );
        })}
      </div>

      {/* Drill-down */}
      <DashboardTour open={tour} onClose={() => setTour(false)} />
      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto rounded-3xl">
          {drill && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-slate-800 flex items-center gap-2"><span className="text-2xl">{lineEmoji(drill.line)}</span> {drill.line} · <span>{shiftLabel(drill.shift)}</span></DialogTitle></DialogHeader>
              <div className="text-xs text-slate-400 -mt-2">Logged by <span className="font-semibold text-slate-600">{drill.saved_by_name || "—"}</span>{drill.saved_at ? ` · ${new Date(drill.saved_at).toLocaleString("en-AU")}` : ""}</div>
              <div className="space-y-4">
                <div>
                  <div className="text-xs font-semibold text-slate-400 mb-1.5">CREW</div>
                  {drill.workers.map((w, i) => (
                    <div key={i} className="flex justify-between text-sm py-1.5 border-b border-slate-100">
                      <span className="text-slate-700 font-medium">{w.no != null ? <span className="text-slate-400">#{w.no} </span> : null}{w.name} <span className="text-slate-400 text-xs">· {w.type}</span></span>
                      <span className="font-num text-slate-700">{kg(w.earned_kg)} kg{canSeeMoney && w.value != null ? <span className="text-emerald-700 font-semibold"> · {money(w.value)}</span> : null}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-400 mb-1.5">PRODUCTS WORKED</div>
                  {(drill.allocations || []).map((a, i) => (
                    <div key={i} className="flex justify-between text-sm py-1.5 border-b border-slate-100">
                      <span className="font-num text-slate-700">{a.reason ? `✏️ Reason: ${a.reason}` : <>{productEmoji(a)} {a.code}</>}</span>
                      <span className="font-num text-slate-700">{kg(a.kg)} kg{canSeeMoney ? (a.unpriced || a.value == null ? <span className="text-amber-600"> · ⚠️ no price set</span> : ` · ${money(a.value)}`) : ""}</span>
                    </div>
                  ))}
                </div>
                {drill.photos?.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-slate-400 mb-1.5 flex items-center gap-1"><Camera size={13} /> PHOTO EVIDENCE</div>
                    <div className="flex gap-2 flex-wrap">{drill.photos.map((p, i) => <PhotoThumb key={i} path={p.storage_path} />)}</div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" className="w-full gap-1.5 rounded-2xl border-teal-200 text-teal-700 h-12" onClick={() => exportShift(drill, canSeeMoney)}><Download size={15} strokeWidth={2.5} /> Shift xlsx</Button>
                  <Button variant="outline" className="w-full gap-1.5 rounded-2xl border-teal-200 text-teal-700 h-12" onClick={() => exportDaily(data.shift_cards, drill.date, canSeeMoney)}><Download size={15} strokeWidth={2.5} /> Whole day xlsx</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  return (
    <div data-testid={testid} className={`rounded-2xl p-3 ${accent ? "btn-grad text-white" : "bg-slate-50"}`}>
      <div className={`text-xs font-medium ${accent ? "text-white/85" : "text-slate-400"}`}>{label}</div>
      <div className={`font-num font-semibold text-xl ${accent ? "text-white" : "text-slate-800"}`}>{value}</div>
    </div>
  );
}

function PhotoThumb({ path }) {
  const [url, setUrl] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let live = true;
    api.get(`/files/${path}`, { responseType: "blob" }).then((r) => { if (live) setUrl(URL.createObjectURL(r.data)); }).catch(() => {});
    return () => { live = false; };
  }, [path]);
  return (
    <>
      <button data-testid="photo-thumb" onClick={() => url && setOpen(true)} className="active:scale-95 transition-transform">
        {url ? <img src={url} alt="evidence" className="w-20 h-20 object-cover rounded-xl border border-slate-200" /> : <div className="w-20 h-20 bg-slate-100 rounded-xl animate-pulse" />}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl p-2 bg-black/90 border-0">
          <DialogHeader><DialogTitle className="sr-only">Photo evidence</DialogTitle></DialogHeader>
          {url && <img data-testid="photo-full" src={url} alt="evidence" className="w-full max-h-[85vh] object-contain rounded-lg" />}
        </DialogContent>
      </Dialog>
    </>
  );
}
